import type { FetchResult } from "./opsPaperworkClientBase";
import { opsFetch, q } from "./opsPaperworkClientBase";
import type { DealsListFacets } from "./ops/opsDealsListQuery";

export type ListOpsDealsParams = {
  status?: string;
  salesperson?: string;
  office?: string;
  type_match?: string;
  page?: number;
  page_size?: number;
};

export type ListOpsDealsPayload = {
  ok: true;
  deals: unknown[];
  total?: number;
  page?: number;
  page_size?: number;
  facets?: DealsListFacets;
};

function dealsQueryExtra(statusOrParams?: string | ListOpsDealsParams): Record<string, string> | undefined {
  const extra: Record<string, string> = {};
  if (typeof statusOrParams === "string") {
    if (statusOrParams) extra.status = statusOrParams;
  } else if (statusOrParams) {
    if (statusOrParams.status) extra.status = statusOrParams.status;
    if (statusOrParams.salesperson) extra.salesperson = statusOrParams.salesperson;
    if (statusOrParams.office) extra.office = statusOrParams.office;
    if (statusOrParams.type_match) extra.type_match = statusOrParams.type_match;
    if (statusOrParams.page != null) extra.page = String(statusOrParams.page);
    if (statusOrParams.page_size != null) extra.page_size = String(statusOrParams.page_size);
  }
  return Object.keys(extra).length ? extra : undefined;
}

export async function getOpsSummary(): Promise<
  FetchResult<{
    ok: true;
    unread_notifications: number;
    unacknowledged_notifications?: number;
    overdue_paperwork_items: number;
    open_tasks_for_me: number;
    unmapped_host_lots?: number;
  }>
> {
  return opsFetch(q("summary"), { method: "GET" });
}

export async function listOpsDeals(
  statusOrParams?: string | ListOpsDealsParams,
): Promise<FetchResult<ListOpsDealsPayload>> {
  return opsFetch(q("deals", dealsQueryExtra(statusOrParams)), { method: "GET" });
}

export async function getOpsDeal(
  id: string,
  opts?: { include?: "attachments"; signMedia?: boolean },
): Promise<
  FetchResult<{
    ok: true;
    deal: unknown;
    external_ids: unknown[];
    paperwork_items: unknown[];
    tasks: unknown[];
    timeline_events?: unknown[];
    attachments?: unknown[];
    salesperson?: unknown;
    deal_type_match?: unknown;
    deal_type_catalog?: unknown;
  }>
> {
  const extra: Record<string, string> = { id };
  if (opts?.include === "attachments") extra.include = "attachments";
  if (opts?.signMedia) extra.sign_media = "1";
  return opsFetch(q("deal", extra), { method: "GET" });
}

export async function postOpsDealTypeMatch(body: {
  deal_id: string;
  action: "assign" | "rematch";
  product_type_id?: string;
  sale_type_id?: string | null;
}): Promise<FetchResult<{ ok: true; match: unknown; catalog: unknown }>> {
  return opsFetch(q("deal-type-match"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function postOpsDealSalesperson(body: {
  deal_id: string;
  action: "request" | "mark_invited" | "add_location";
}): Promise<FetchResult<{ ok: true; salesperson: unknown }>> {
  return opsFetch(q("salesperson"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function getOpsPaperworkRules(): Promise<FetchResult<{ ok: true; rules: unknown; ruleset_key?: string; from_fallback?: boolean }>> {
  return opsFetch(q("paperwork-rules"), { method: "GET" });
}

export async function seedOpsPaperworkRuleSet(): Promise<FetchResult<{ ok: true; rule_set: unknown }>> {
  return opsFetch(q("seed-rule-set"), { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
}

export async function postOpsPreviewManifest(dealContext: Record<string, string>): Promise<FetchResult<{ ok: true; items: unknown[] }>> {
  return opsFetch(q("preview-manifest"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_context: dealContext }),
  });
}

export async function postOpsTimelineEvent(body: { deal_id: string; event_type: string; body?: string }): Promise<FetchResult<{ ok: true }>> {
  return opsFetch(q("timeline-event"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function getOpsDealUploadStub(): Promise<
  FetchResult<{
    ok: true;
    message?: string;
    upload_enabled?: boolean;
    drive_configured?: boolean;
    drive_org_enabled?: boolean;
    drive_parent_id_present?: boolean;
  }>
> {
  return opsFetch(q("deal-upload-intent"), { method: "GET" });
}

export async function getOpsDigestPreviewStub(reportDay?: string): Promise<
  FetchResult<{
    ok: true;
    digest_enabled: boolean;
    report_day: string;
    preview: { title: string; body: string; plain_text: string };
    email_copy_stub: string;
  }>
> {
  return opsFetch(q("digest-preview", reportDay ? { report_day: reportDay } : undefined), { method: "GET" });
}

export async function getOpsOwnershipDigestPreview(reportDay?: string) {
  return getOpsDigestPreviewStub(reportDay);
}

export async function postOpsOwnershipDigestSend(body: {
  report_day?: string;
  force?: boolean;
}): Promise<
  FetchResult<{
    ok: true;
    recipient_count: number;
    sent_at: string;
    report_day: string;
    preview: { title: string; body: string; plain_text: string };
    email_copy_stub: string;
  }>
> {
  return opsFetch(q("ownership-digest-send"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchOpsOwnershipDigestSchedule(body: {
  encouraging_note?: string | null;
  local_send_time?: string | null;
  broadcast_all_members?: boolean;
}): Promise<FetchResult<{ ok: true; schedule: unknown }>> {
  return opsFetch(q("ownership-digest-schedule"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listOpsTemplates(): Promise<FetchResult<{ ok: true; templates: unknown[] }>> {
  return opsFetch(q("templates"), { method: "GET" });
}

export async function listOpsNotifications(): Promise<FetchResult<{ ok: true; notifications: unknown[] }>> {
  return opsFetch(q("notifications"), { method: "GET" });
}

export async function listOpsMessageThreads(): Promise<
  FetchResult<{ ok: true; threads: unknown[]; unread_count: number; unacked_count: number }>
> {
  return opsFetch(q("notification-threads"), { method: "GET" });
}

export async function listOpsThreadMessages(threadId: string): Promise<
  FetchResult<{ ok: true; thread_id: string; messages: unknown[]; participant_receipts?: unknown[] }>
> {
  return opsFetch(q("notification-thread-messages", { thread_id: threadId }), { method: "GET" });
}

export async function listOpsNotificationMembers(): Promise<
  FetchResult<{
    ok: true;
    members: Array<{ user_id: string; trust_level: number; name: string; email: string | null }>;
    viewer?: { user_id: string; trust_level: number };
  }>
> {
  return opsFetch(q("notification-members"), { method: "GET" });
}

export async function postOpsThreadReply(body: {
  thread_id: string;
  body: string;
  title?: string;
  message_class?: string;
  requires_ack?: boolean;
  ack_deadline_at?: string | null;
  recipient_user_ids?: string[];
  deal_id?: string | null;
}): Promise<FetchResult<{ ok: true; thread_id: string; sent: number; message: unknown }>> {
  return opsFetch(q("notification-thread-reply"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function createOpsThread(body: {
  body: string;
  title?: string;
  message_class?: string;
  requires_ack?: boolean;
  sender_locked?: boolean;
  ack_deadline_at?: string | null;
  recipient_user_ids: string[];
  deal_id?: string | null;
}): Promise<FetchResult<{ ok: true; thread_id: string; sent: number; message: unknown }>> {
  return opsFetch(q("notification-thread-create"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function archiveOpsNotification(body: {
  id?: string;
  thread_id?: string;
}): Promise<
  FetchResult<{
    ok: true;
    archived: number;
    archived_ids: string[];
    skipped: Array<{ id: string; reason: string }>;
  }>
> {
  return opsFetch(q("notification-archive"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
